'use client'

import { CircleCheck, Info, LoaderCircle, OctagonX, TriangleAlert } from 'lucide-react'
import { useTheme } from 'next-themes'
import { Toaster as Sonner, toast } from 'sonner'

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme()

  return (
    <Sonner
      className="toaster group"
      icons={{
        error: <OctagonX className="h-4 w-4" />,
        info: <Info className="h-4 w-4" />,
        loading: <LoaderCircle className="h-4 w-4 animate-spin" />,
        success: <CircleCheck className="h-4 w-4" />,
        warning: <TriangleAlert className="h-4 w-4" />,
      }}
      theme={theme as ToasterProps['theme']}
      toastOptions={{
        classNames: {
          actionButton: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
          description: 'group-[.toast]:text-muted-foreground',
          error: '!border-error-border !bg-error-bg !text-error',
          success: '!border-success-border !bg-success-bg !text-success',
          info: '!border-info-border !bg-info-bg !text-info',
          loading: '!border-info-border !bg-info-bg !text-info',
          warning: '!border-warning-border !bg-warning-bg !text-warning',
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
        },
      }}
      {...props}
    />
  )
}

const asyncToast = async (
  promise: Promise<unknown>,
  {
    loading,
    success,
    error,
  }: {
    loading: (id: string) => Parameters<typeof toast.loading>
    success: (id: string) => Parameters<typeof toast.success>
    error: (id: string) => Parameters<typeof toast.error>
  },
) => {
  const id = Math.random().toString(36).substring(2, 9)
  const [loadingMessage, loadingOptions] = loading(id)
  const [successMessage, successOptions] = success(id)
  const [errorMessage, errorOptions] = error(id)

  toast.loading(loadingMessage, {
    ...loadingOptions,
    id,
    duration: Infinity,
  })

  try {
    await promise
    toast.success(successMessage, {
      ...successOptions,
      id,
      duration: undefined,
    })
  } catch (e) {
    toast.error(errorMessage, {
      ...errorOptions,
      id,
      duration: undefined,
    })
  }
}

export { Toaster, asyncToast }
